import React, { useMemo } from 'react';
import { setChatlistOpenHover } from '../../../Store/Actions/chatlistActions';
import AssistentList from '../components/AssistentList';
import Input from '../../Utility/FormElements/Input';
import ChatListItem from '../ChatListItem/ChatListItem';
import useChatList from '../../../Hooks/useChatList';

export function ChatList() {
    const {
        ChatListActions,
        i18n,
        allChatsLoaded,
        chatlistOpen,
        chatlistOpenHover,
        ListItemEdit,
        setListItemEdit,
        filterValue,
        setFilter,
        themeLogo,
        chatsFetched,
        groupedChats,
        currentURL,
        dispatch,
    } = useChatList();

    const chatListItems = useMemo(() => {
        if (!groupedChats || Object.entries(groupedChats).length === 0) return null;

        return Object.entries(groupedChats).map(([headline, chatlistItems], i) => (
            <div className="gap-1 flex flex-col mb-4" key={i}>
                <p className="font-normal text-chatlist-timeSeparatorColor py-0 py-2 pl-1 m-0 sticky top-0 bg-chatlist-background z-10">
                    {headline}
                </p>
                {chatlistItems?.map((chatlistItem: any, j: any) => (
                    <ChatListItem
                        key={j}
                        setCanEdit={setListItemEdit}
                        editItem={ListItemEdit}
                        chatIdentifier={chatlistItem.identifier}
                        currentUrl={currentURL}
                        deleteChat={ChatListActions.deleteChatHandler}
                        chatTitle={chatlistItem.title || 'n/a'}
                        updateChatTitle={ChatListActions.updateTitle}
                        assistantTitle={chatlistItem.assistantTitle}
                        assistantImage={chatlistItem.assistantImage}
                    />
                ))}
            </div>
        ));
    }, [groupedChats, ListItemEdit, setListItemEdit, currentURL, ChatListActions]);

    const AssistentListMemo = useMemo(() => <AssistentList />, []);

    return (
        <div
            className={`relative transition-all duration-300 h-[var(--app-height)] ${
                chatlistOpen ? 'w-[300px]' : 'w-0'
            }`}
            onMouseLeave={() => dispatch(setChatlistOpenHover(false))}
        >
            <div
                className={`fixed z-[15] transition-all duration-300 h-full overflow-x-hidden border-layout-borderColor ${
                    chatlistOpenHover || chatlistOpen ? 'w-[300px] border-r' : 'w-[0px]'
                }`}
            >
                <div className="absolute">
                    <div
                        className={`joyride-step-chat-list flex flex-col transition-all duration-100 h-[var(--app-height)] pb-0 chat-list-container min-w-[300px] max-w-[300px] relative left-0`}
                    >
                        <div className="bg-chatlist-background flex flex-col h-full">
                            <div className="w-full min-h-[100px] flex justify-center items-center">
                                <div className="flex items-center w-full h-full pl-[75px]">
                                    <button
                                        className="relative h-[50px] w-full text-xl font-semibold text-white"
                                        onClick={(e) => {
                                            ChatListActions.navigateToHome();
                                        }}
                                    >
                                        {themeLogo && (
                                            <img
                                                className="absolute m-auto h-auto max-w-[190px] max-h-[50px] pl-6 top-0 bottom-0 left-0 transition-all duration-300"
                                                src={themeLogo}
                                                alt={document.title}
                                            />
                                        )}
                                    </button>
                                </div>
                            </div>
                            <div className="scroll-smooth overflow-x-hidden overflow-y-auto flex flex-col chat-list-list grow">
                                <div className="pt-3 px-6">
                                    <div className="joyride-step-navigation-userprompts w-full pb-0">
                                        <h5 className="font-normal text-layout-textcolor py-0 pl-1 mb-4">
                                            {i18n.translate({ id: 'chatlist.assistants', sourceName: 'Main' })}
                                        </h5>
                                        {AssistentListMemo}
                                    </div>

                                    <hr className="border-t border-layout-borderColor w-full my-6"></hr>

                                    <Input
                                        value={filterValue}
                                        onChange={(value) => setFilter(value)}
                                        placeholder={i18n.translate({ id: 'filter.placeholder', sourceName: 'Main' })}
                                        className="w-full"
                                    />
                                    <hr className="border-t border-layout-borderColor w-full my-6"></hr>
                                </div>

                                <div className={`scroll-smooth overflow-x-hidden overflow-y-auto px-6`}>
                                    {allChatsLoaded.length !== 0 ? (
                                        chatListItems ?? (
                                            <p>
                                                {i18n.translate({
                                                    id: 'filter.notFound',
                                                    sourceName: 'ChatList',
                                                })}
                                            </p>
                                        )
                                    ) : !chatsFetched ? (
                                        <div className="flex items-center justify-center w-full">
                                            <div className="relative flex items-center justify-center h-10 w-10">
                                                <div className="absolute inline-block w-10 h-10 border-4 border-t-primary border-gray-300 rounded-full animate-spin"></div>
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="text-center">
                                            {i18n.translate({
                                                id: 'noChats',
                                                sourceName: 'ChatList',
                                            })}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
